// A car at a glance: its name and what it is, its TUNE MFD, its next service in its own units, and the dashboard on
// its screen. The whole card opens the car.
import { METRIC, type components } from '@tune/shared';
import { Link } from 'react-router';
import { describeCar, seenThisWeek, serviceText } from '../../lib/car';
import { agoText } from '../../lib/words';
import { useUnits } from '../../queries';
import { cx, Dot } from '../../ui';
import { CaretRightIcon, GaugeIcon, WrenchIcon } from '../../ui/icons';
import s from './CarCard.module.css';

type Vehicle = components['schemas']['Vehicle'];

/** The car's MFD in a few words: "MFD seen 2 hours ago". */
export function mfdText(car: Pick<Vehicle, 'mfd'>, now: number): string {
  if (!car.mfd) {
    return 'No MFD in this car';
  }
  return car.mfd.lastSeenAt ? `MFD seen ${agoText(car.mfd.lastSeenAt, now)}` : 'MFD not seen yet';
}

export function CarCard({ car, now }: { car: Vehicle; now: number }) {
  const units = useUnits(car.id) ?? METRIC;
  const service = serviceText(car.serviceDue, units);
  const what = describeCar(car);
  return (
    <Link to={`/garage/${car.id}`} className={s.car}>
      <span className={s.name}>
        <span className={s.nickname}>{car.nickname}</span>
        <CaretRightIcon size={16} weight="bold" aria-hidden="true" />
      </span>
      {what && <span className={s.what}>{what}</span>}
      <span className={s.facts}>
        <span className={s.fact}>
          <Dot tone={seenThisWeek(car.mfd?.lastSeenAt ?? null, now) ? 'good' : 'neutral'} />
          {mfdText(car, now)}
        </span>
        {service && (
          <span className={cx(s.fact, service.overdue && s.warn)}>
            <WrenchIcon size={16} aria-hidden="true" />
            Service {service.text.charAt(0).toLowerCase() + service.text.slice(1)}
          </span>
        )}
        {car.showing && (
          <span className={s.fact}>
            <GaugeIcon size={16} aria-hidden="true" />
            Showing {car.showing}
          </span>
        )}
      </span>
    </Link>
  );
}

/** Cars in a grid of cards that fits the width. */
export function CarCards({ cars, now, label }: { cars: readonly Vehicle[]; now: number; label?: string }) {
  return (
    <ul className={s.cards} role="list" aria-label={label}>
      {cars.map((car) => (
        <li key={car.id}>
          <CarCard car={car} now={now} />
        </li>
      ))}
    </ul>
  );
}
